import { makeAutoObservable } from 'mobx'
import { ChatbotFollowUpAssertiveness, type IChatbotFollowUpAssertiveness } from 'entities/Chatbot'
import { formatWidgetMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import type { AutoFollowUpSectionStore } from './autoFollowUpSectionStore'

export const STEP_HEADERS = {
  shouldFollowUp: 'Should this agent follow up?',
  followUpWindow: 'How long should the follow-up window be?',
  assertiveness: 'How often should the agent follow up?',
  sessionLifetime: 'When should the session end if there is no reply?',
}

export const FOLLOW_UP_ENABLE_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export const FOLLOW_UP_WINDOW_OPTIONS = [
  { id: 3, label: '3 days' },
  { id: 7, label: '7 days' },
  { id: 14, label: '14 days' },
]

export type IFollowUpAssertivenessOption = {
  id: IChatbotFollowUpAssertiveness
  label: string
  rightLabel: string
}

export const FOLLOW_UP_ASSERTIVENESS_OPTIONS: IFollowUpAssertivenessOption[] = [
  { id: ChatbotFollowUpAssertiveness.Subtle, label: 'Subtle', rightLabel: 'Few reminders' },
  { id: ChatbotFollowUpAssertiveness.Steady, label: 'Steady', rightLabel: 'Balanced reminders' },
  {
    id: ChatbotFollowUpAssertiveness.Assertive,
    label: 'Assertive',
    rightLabel: 'Frequent reminders',
  },
]

export const SESSION_LIFETIME_OPTIONS = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  label: i === 0 ? 'In 1 day' : `In ${i + 1} days`,
}))

export class AutoFollowUpWidgetStore {
  step = 1
  focusedIndex = 0
  isFollowUp: boolean | null = null
  localFollowUpWindow: number
  private _step3FocusedIndex: number | null = null

  constructor(readonly sectionStore: AutoFollowUpSectionStore, readonly chatStore: ChatStore) {
    this.localFollowUpWindow = sectionStore.followUpWindow
    makeAutoObservable(this)
  }

  get totalSteps() {
    return this.isFollowUp === true ? 3 : 2
  }

  get isLastStep() {
    return this.step === this.totalSteps && this.step > 1
  }

  get currentListLength() {
    switch (this.step) {
      case 1:
        return FOLLOW_UP_ENABLE_OPTIONS.length
      case 2:
        return this.isFollowUp ? FOLLOW_UP_WINDOW_OPTIONS.length : SESSION_LIFETIME_OPTIONS.length
      case 3:
        return FOLLOW_UP_ASSERTIVENESS_OPTIONS.length
      default:
        return 0
    }
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    return Math.min(this.focusedIndex, this.currentListLength - 1)
  }

  get submitMessage(): string {
    if (this.isFollowUp) {
      return formatWidgetMessage([
        { q: STEP_HEADERS.shouldFollowUp, a: 'Skip' },
        { q: STEP_HEADERS.followUpWindow, a: 'Skip' },
        { q: STEP_HEADERS.assertiveness, a: 'Skip' },
      ])
    }
    if (!this.isFollowUp && this.isFollowUp !== null) {
      return formatWidgetMessage([
        { q: STEP_HEADERS.shouldFollowUp, a: 'Skip' },
        { q: STEP_HEADERS.sessionLifetime, a: 'Skip' },
      ])
    }
    return formatWidgetMessage([{ q: STEP_HEADERS.shouldFollowUp, a: 'Skip' }])
  }

  private get _windowFocusIndex() {
    return Math.max(
      0,
      FOLLOW_UP_WINDOW_OPTIONS.findIndex((option) => option.id === this.localFollowUpWindow)
    )
  }

  private get _assertivenessFocusIndex() {
    return Math.max(
      0,
      FOLLOW_UP_ASSERTIVENESS_OPTIONS.findIndex(
        (option) => option.id === this.sectionStore.assertiveness
      )
    )
  }

  private get _lifetimeFocusIndex() {
    return Math.max(
      0,
      SESSION_LIFETIME_OPTIONS.findIndex(
        (option) => option.id === this.sectionStore.sessionLifetime
      )
    )
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    this.focusedIndex = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
  }

  private advanceStep = () => {
    this.step++
    this.focusedIndex = 0
  }

  goBack = () => {
    if (this.step <= 1) return
    if (this.step === 3) this._step3FocusedIndex = this.focusedIndex
    this.step--

    switch (this.step) {
      case 1:
        this.focusedIndex = !this.isFollowUp ? 1 : 0
        break
      case 2:
        this.focusedIndex = this._windowFocusIndex
        break
      default:
        this.focusedIndex = 0
    }
  }

  reset = () => {
    this.step = 1
    this.focusedIndex = 0
    this.isFollowUp = null
    this.localFollowUpWindow = this.sectionStore.followUpWindow
    this._step3FocusedIndex = null
  }

  private _selectOnStep1 = () => {
    const option = FOLLOW_UP_ENABLE_OPTIONS[this.safeIndex]
    this.isFollowUp = option.id === 'yes'
    this.advanceStep()
    this.focusedIndex = this.isFollowUp ? this._windowFocusIndex : this._lifetimeFocusIndex
  }

  private _selectOnStep2Window = () => {
    const option = FOLLOW_UP_WINDOW_OPTIONS[this.safeIndex]
    this.localFollowUpWindow = option.id
    this.advanceStep()
    this.focusedIndex = this._step3FocusedIndex ?? this._assertivenessFocusIndex
  }

  handleSelect = () => {
    switch (this.step) {
      case 1:
        this._selectOnStep1()
        break
      case 2:
        if (this.isFollowUp) this._selectOnStep2Window()
        break
    }
  }

  selectAt = (index: number) => {
    this.focusedIndex = index
    this.handleSelect()
  }

  handleSubmit = () => {
    let message: string
    let applyConfig: () => void

    if (!this.isFollowUp) {
      const option = SESSION_LIFETIME_OPTIONS[this.safeIndex]
      message = formatWidgetMessage([
        { q: STEP_HEADERS.shouldFollowUp, a: 'No' },
        { q: STEP_HEADERS.sessionLifetime, a: option.label },
      ])
      applyConfig = () => {
        this.chatStore.applySectionUpdateSilently('autoFollowUp', () => {
          this.sectionStore.setSessionLifetime(option.id)
          this.sectionStore.setEnabled(false)
        })
      }
      this.chatStore.setLocalWidgetState({ settings: { enable_followup: false } })
    } else {
      const followUpWindowLabel =
        FOLLOW_UP_WINDOW_OPTIONS.find((o) => o.id === this.localFollowUpWindow)?.label ?? ''
      const option = FOLLOW_UP_ASSERTIVENESS_OPTIONS[this.safeIndex]
      const localFollowUpWindow = this.localFollowUpWindow
      message = formatWidgetMessage([
        { q: STEP_HEADERS.shouldFollowUp, a: 'Yes' },
        { q: STEP_HEADERS.followUpWindow, a: followUpWindowLabel },
        { q: STEP_HEADERS.assertiveness, a: option.label },
      ])
      applyConfig = () => {
        this.chatStore.applySectionUpdateSilently('autoFollowUp', () => {
          this.sectionStore.setFollowUpWindow(localFollowUpWindow)
          this.sectionStore.setAssertiveness(option.id)
          this.sectionStore.setEnabled(true)
        })
      }
      this.chatStore.setLocalWidgetState({
        settings: {
          enable_followup: true,
          followup_window: localFollowUpWindow,
          followup_assertiveness: option.id,
        },
      })
    }

    this.chatStore.sendMessage(message, applyConfig)
  }

  handleClose = () => {
    const message = this.submitMessage
    this.chatStore.sendMessage(message)
  }
}
